<template>
  <ElTabs v-model="activePane" :class="{ header }" class="easy-tabs">
    <div v-if="header" class="pad-space" />
    <slot />

	<EasyTabPane v-for="item of newPanes" :key="item.name" :name="item.name" :label="item.label" >
		<component :is="item.component" v-bind="item.props" />
	</EasyTabPane>
  </ElTabs>
</template>

<script lang="ts">
import { defineComponent, ref, Ref, computed, PropType, Component, VNodeProps, AllowedComponentProps } from 'vue';
import { ElTabs } from 'element-plus';
import { isObject } from 'lodash-es';
import EasyTabPane from './EasyTabPane.vue';

type PaneDesc = {
	name: string,
	label: string,
	component: Component, 
	props: Record<string, any>
};

// 通过组件类型，推断属性类型
type ComponentProps<C extends Component> = C extends new (...args: any) => any
  ? Omit<
      InstanceType<C>['$props'],
      keyof VNodeProps | keyof AllowedComponentProps
    >
  : never;

export function paneDef<C extends Component>(name: string, label: string, 
	component: C, props: ComponentProps<C>) : PaneDesc {
	return {
		name,
		label,
		component,
		props: props
	}
}

/**
 * 将element-plus的el-tabs组件的v-model ref转到reactive
 */
export default defineComponent({
  extends: [ElTabs],

  props: {
    header: { type: Boolean }, // 顶部显示
	panes: { type: Array as PropType<Array<PaneDesc>>, require: false, default: undefined } // 通过属性(而不是slot)指定tab子项, 可以减少页面更新
  },

  setup(props, ctx) {
	// 当前active的页
	let activePane: Ref<string>;

	let newPanes: Ref<Array<PaneDesc>>;
	if(props.panes?.length) {
		activePane = ref(props.panes[0].name);

		const panePropsMap: Record<string, Record<string, any>> = {};

		newPanes = computed(() => {
			return props.panes!.map(e => {
				// 上一次使用的属性值
				const preProps = panePropsMap[e.name];
				// 本次要使用的属性值
				let curProps: Record<string, any>;

				if(activePane.value == e.name || preProps === undefined) {
					curProps = e.props;
					panePropsMap[e.name] = curProps;
				} else {
					curProps = preProps;
				}

				return {
					name: e.name,
					label: e.label,
					component: e.component,
					props: curProps
				}
			});
		});
	}
	else {
		newPanes = ref([]);

		// 找到第一个tab项
		let panes = ctx.slots.default?.() ?? [];
		let pane = panes.find((e) => {
			if (!isObject(e.type)) {
				return false;
			}

			return (e.type as any).name == 'ElTabPane' || e.type == EasyTabPane;
		});
    	activePane = ref((pane?.props?.name as string) || '');
	}

    return { activePane, newPanes, ElTabs, EasyTabPane };
  }
});
</script>

<style lang="scss" scoped>
.easy-tabs {
  flex-grow: 1;
  display: flex;
  flex-direction: column;

  :deep(.el-tabs__content) {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }

  :deep(.el-tab-pane) {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }

  :deep(.el-tabs__item:nth-of-type(2)) {
    margin-left: 16px;
  }
}

.easy-popup .easy-tabs {
  padding: 0 16px;
}

.easy-tabs.header {
  :deep(.el-tabs__header) {
    position: absolute;
    left: 0px;
    top: 0px;
    width: 100%;
    height: 40px;
    background-color: white;
  }

  .pad-space {
    height: 38px;
  }
}
</style>
